import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { authApi } from '../../services/api';
import { PlusIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import type { User } from '../../types';


const UserList: React.FC = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState<any[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(15);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setIsLoading(true);
      setError('');
      const response = await authApi.listUsers();
      setUsers(response as User[]);
      setFilteredUsers(response as User[]);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erro ao carregar usuários');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const filtered = users.filter(user => 
      user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (user.full_name && user.full_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      `${user.first_name} ${user.last_name}`.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredUsers(filtered);
    setCurrentPage(1);
  }, [searchTerm, users]);

  const totalPages = Math.ceil(filteredUsers.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const currentUsers = filteredUsers.slice(startIndex, endIndex);

  const handleDeleteUser = async (userId: number) => {
    if (!window.confirm('Tem certeza que deseja excluir este usuário? Esta ação não pode ser desfeita.')) {
      return;
    }

    try {
      await authApi.deleteUser(userId);
      const updatedUsers = users.filter(u => u.id !== userId);
      setUsers(updatedUsers);
      setFilteredUsers(updatedUsers.filter(user => 
        user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (user.full_name && user.full_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        `${user.first_name} ${user.last_name}`.toLowerCase().includes(searchTerm.toLowerCase())
      ));
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erro ao excluir usuário');
    }
  };

  if (!user?.is_staff) {
    return (
    <div className="space-y-4">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          Acesso negado. Apenas administradores podem acessar esta página.
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
    <div className="space-y-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-2 text-secondary-600">Carregando usuários...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="mb-4 flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
        <div>
          <h1 className="text-base font-bold text-secondary-900">Usuários</h1>
          <p className="text-xs text-secondary-600">Gerencie todos os usuários do sistema</p>
        </div>
        <Link
          to="/users/new"
          className="btn-primary flex items-center w-fit whitespace-nowrap"
        >
          <PlusIcon className="h-4 w-4 mr-1.5" />
          Novo Usuário
        </Link>
      </div>

      {/* Search */}
      <div className="card mb-4">
        <input
          type="text"
          placeholder="Buscar usuários..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="input-field"
        />
      </div>

      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Responsive Cards for Mobile */}
      <div className="block lg:hidden space-y-3">
        {currentUsers.map((userItem) => (
          <div key={userItem.id} className="card">
            <div className="flex justify-between items-start mb-2">
              <div className="flex-1">
                <h3 className="text-sm font-medium text-secondary-900">
                  {userItem.full_name || `${userItem.first_name} ${userItem.last_name}`.trim() || userItem.username}
                </h3>
                <p className="text-xs text-secondary-500">@{userItem.username}</p>
                <p className="text-xs text-secondary-600">{userItem.email}</p>
              </div>
              <div className="flex space-x-1">
                {userItem.is_superuser && (
                  <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                    Super Admin
                  </span>
                )}
                {userItem.is_staff && !userItem.is_superuser && (
                  <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                    Admin
                  </span>
                )}
                {!userItem.is_staff && (
                  <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">
                    Usuário
                  </span>
                )}
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-2 text-xs text-secondary-600 mb-2">
              <div><span className="font-medium">Cadastro:</span> {userItem.date_joined ? new Date(userItem.date_joined).toLocaleDateString('pt-BR') : '-'}</div>
              <div><span className="font-medium">Último Login:</span> {userItem.last_login ? new Date(userItem.last_login).toLocaleDateString('pt-BR') : 'Nunca'}</div>
            </div>
            
            <div className="flex justify-end space-x-2">
              <Link to={`/users/${userItem.id}/edit`} className="text-primary-600 hover:text-primary-900 p-1" title="Editar">
                <PencilIcon className="h-4 w-4" />
              </Link>
              {userItem.id !== user?.id && (
                <button onClick={() => handleDeleteUser(userItem.id)} className="text-red-600 hover:text-red-900 p-1" title="Excluir">
                  <TrashIcon className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
      
      {/* Table for Desktop */}
      <div className="hidden lg:block card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-secondary-50">
              <tr>
                <th className="px-2 py-1.5 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">Usuário</th>
                <th className="px-2 py-1.5 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">Email</th>
                <th className="px-2 py-1.5 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">Tipo</th>
                <th className="px-2 py-1.5 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">Cadastro</th>
                <th className="px-2 py-1.5 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">Login</th>
                <th className="px-2 py-1.5 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">Ações</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-secondary-100">
              {currentUsers.map((userItem) => (
                <tr key={userItem.id} className="hover:bg-secondary-25">
                  <td className="px-2 py-1.5">
                    <div className="text-xs font-medium text-secondary-900 truncate max-w-32">
                      {userItem.full_name || `${userItem.first_name} ${userItem.last_name}`.trim() || userItem.username}
                    </div>
                    <div className="text-xs text-secondary-500">@{userItem.username}</div>
                  </td>
                  <td className="px-2 py-1.5 text-xs text-secondary-900 truncate max-w-40">{userItem.email}</td>
                  <td className="px-2 py-1.5">
                    {userItem.is_superuser && (
                      <span className="inline-flex px-1.5 py-0.5 text-xs font-medium rounded bg-red-100 text-red-800">Super Admin</span>
                    )}
                    {userItem.is_staff && !userItem.is_superuser && (
                      <span className="inline-flex px-1.5 py-0.5 text-xs font-medium rounded bg-blue-100 text-blue-800">Admin</span>
                    )}
                    {!userItem.is_staff && (
                      <span className="inline-flex px-1.5 py-0.5 text-xs font-medium rounded bg-gray-100 text-gray-800">Usuário</span>
                    )}
                  </td>
                  <td className="px-2 py-1.5 text-xs text-secondary-500">
                    {userItem.date_joined ? new Date(userItem.date_joined).toLocaleDateString('pt-BR') : '-'}
                  </td>
                  <td className="px-2 py-1.5 text-xs text-secondary-500">
                    {userItem.last_login ? new Date(userItem.last_login).toLocaleDateString('pt-BR') : 'Nunca'}
                  </td>
                  <td className="px-2 py-1.5">
                    <div className="flex space-x-1">
                      <Link to={`/users/${userItem.id}/edit`} className="text-primary-600 hover:text-primary-900 p-0.5" title="Editar">
                        <PencilIcon className="h-3.5 w-3.5" />
                      </Link>
                      {userItem.id !== user?.id && (
                        <button onClick={() => handleDeleteUser(userItem.id)} className="text-red-600 hover:text-red-900 p-0.5" title="Excluir">
                          <TrashIcon className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-white px-3 py-2 flex items-center justify-between border-t border-secondary-200">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-3 py-1.5 border border-secondary-300 text-sm font-medium rounded-md text-secondary-700 bg-white hover:bg-secondary-50 disabled:opacity-50"
              >
                Anterior
              </button>
              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="ml-3 relative inline-flex items-center px-3 py-1.5 border border-secondary-300 text-sm font-medium rounded-md text-secondary-700 bg-white hover:bg-secondary-50 disabled:opacity-50"
              >
                Próximo
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <p className="text-xs text-secondary-700">
                Página <span className="font-medium">{currentPage}</span> de{' '}
                <span className="font-medium">{totalPages}</span> ({filteredUsers.length} usuários)
              </p>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-2 py-1 rounded-l-md border border-secondary-300 bg-white text-xs font-medium text-secondary-500 hover:bg-secondary-50 disabled:opacity-50"
                  >
                    Anterior
                  </button>
                  <button
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className="relative inline-flex items-center px-2 py-1 rounded-r-md border border-secondary-300 bg-white text-xs font-medium text-secondary-500 hover:bg-secondary-50 disabled:opacity-50"
                  >
                    Próximo
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {filteredUsers.length === 0 && !isLoading && (
        <div className="text-center py-8">
          <p className="text-secondary-500">
            {searchTerm ? 'Nenhum usuário encontrado para a busca' : 'Nenhum usuário encontrado'}
          </p>
        </div>
      )}
    </div>
  );
};

export default UserList;